/**
 * Wiederverwendbares Table→Card-Pattern für Mobile.
 * Desktop (≥768px): normale Tabelle. Mobile (<768px): jede Zeile als Card.
 * Nutzer: Gewächshaus (Hanf-/Tabak-Bestand), Börse (Marktpreistabellen).
 */
import { DESKTOP_LAYOUT_QUERY, MOBILE_LAYOUT_QUERY } from './mobile-layout.js';

export interface RtColumn {
  header: string;
}

export interface RtCardField {
  label: string;
  /** Beliebiges HTML (z.B. mit color-Styles oder Badges). */
  value: string;
}

export interface RtRow {
  /** Fertiges <tr>…</tr> HTML für Desktop-Tabellenansicht (darf mehrere <tr> enthalten). */
  tableHtml: string;
  /** HTML für den Card-Kopf: Item-Name + Badges. */
  cardHeadHtml: string;
  /** Label:Value-Paare für das 2-spaltige Card-Body-Grid. */
  cardFields: RtCardField[];
  /** Extra-Attribute am .rt-card-Element, z.B. data-boerse-action für Event-Delegation. */
  rowAttrs?: string;
  /** Optionaler Block unterhalb des Grid, z.B. Action-Button oder Preisverlauf-Chart. */
  cardBottomHtml?: string;
}

export function renderResponsiveTable(columns: RtColumn[], rows: RtRow[]): string {
  const theadHtml = columns.map((c) => `<th class="rt-th">${c.header}</th>`).join('');
  const tbodyHtml = rows.map((r) => r.tableHtml).join('');

  const cardsHtml = rows
    .map(
      (r) => `
      <div class="rt-card"${r.rowAttrs ? ` ${r.rowAttrs}` : ''}>
        <div class="rt-card-head">${r.cardHeadHtml}</div>
        <div class="rt-card-body">
          ${r.cardFields
            .map((f) => `<span class="rt-label">${f.label}</span><span class="rt-value">${f.value}</span>`)
            .join('')}
        </div>
        ${r.cardBottomHtml ?? ''}
      </div>`,
    )
    .join('');

  return `
    <div class="rt-wrap">
      <div class="rt-desktop">
        <table class="rt-table">
          <thead><tr>${theadHtml}</tr></thead>
          <tbody>${tbodyHtml}</tbody>
        </table>
      </div>
      <div class="rt-mobile">${cardsHtml}</div>
    </div>`;
}

export const RESPONSIVE_TABLE_CSS = `
  .rt-wrap{width:100%;}

  @media ${DESKTOP_LAYOUT_QUERY} {
    .rt-desktop{display:block;}
    .rt-mobile{display:none;}
  }
  @media ${MOBILE_LAYOUT_QUERY} {
    .rt-desktop{display:none;}
    .rt-mobile{display:flex;flex-direction:column;gap:var(--space-2);}
  }

  .rt-table{width:100%;border-collapse:collapse;font-family:var(--font-mono);
    font-size:var(--font-size-sm);}
  .rt-th{padding:4px 8px;text-align:left;font-size:var(--font-size-xs);
    letter-spacing:.08em;color:var(--text-muted);border-bottom:1px solid var(--border);
    text-transform:uppercase;}

  .rt-card{border:1px solid var(--border);border-radius:var(--radius);
    padding:var(--space-2) var(--space-3);background:var(--bg-elevated);}
  .rt-card[style*="cursor:pointer"]:active{background:var(--bg-surface);}
  .rt-card-head{display:flex;align-items:center;gap:var(--space-2);
    margin-bottom:var(--space-2);flex-wrap:wrap;}
  .rt-card-name{font-family:var(--font-mono);font-size:var(--font-size-sm);
    color:var(--text-primary);letter-spacing:.06em;text-transform:uppercase;}
  .rt-card-body{display:grid;grid-template-columns:1fr 1fr;
    gap:2px var(--space-3);align-items:baseline;}
  .rt-label{font-family:var(--font-mono);font-size:var(--font-size-xs);
    color:var(--text-muted);letter-spacing:.06em;text-transform:uppercase;}
  .rt-value{font-family:var(--font-mono);font-size:var(--font-size-xs);
    color:var(--text-primary);}

  .rt-badge{font-size:var(--font-size-xs);letter-spacing:.05em;
    padding:1px 5px;border-radius:var(--radius-sm);border:1px solid;
    display:inline-block;text-transform:uppercase;}
`;

/** Injiziert die Responsive-Table-Styles einmalig in den <head> (idempotent). */
export function injectResponsiveTableStyles(): void {
  if (document.getElementById('rt-styles')) return;
  const s = document.createElement('style');
  s.id = 'rt-styles';
  s.textContent = RESPONSIVE_TABLE_CSS;
  document.head.appendChild(s);
}
